import api from "./api";

const FIRMWARE_PATH = "/firmware";
const FILTER_KEYS = [
  "search", "model", "firmware_type", "android_version", "status",
  "firmware_version", "firmware_url", "apdb_url", "updated_date",
];
export const FIRMWARE_EXPORT_FALLBACK_FILENAME = "firmware_export.xlsx";
export const FIRMWARE_BACKUP_FALLBACK_FILENAME = "firmware_backup.xlsx";

function buildFirmwareFilterParams(filters = {}) {
  const params = {};
  FILTER_KEYS.forEach((key) => {
    const value = String(filters[key] || "").trim();
    if (value) params[key] = value;
  });
  return params;
}

export function buildFirmwareListParams(filters = {}) {
  const params = {
    ...buildFirmwareFilterParams(filters),
    page: Number(filters.page) > 0 ? Number(filters.page) : 1,
    page_size: Number(filters.page_size) > 0 ? Number(filters.page_size) : 20,
  };
  return params;
}

export function buildFirmwareExportParams(filters = {}) {
  return buildFirmwareFilterParams(filters);
}

export async function listFirmware(filters = {}, { signal } = {}) {
  const { data } = await api.get(FIRMWARE_PATH, {
    params: buildFirmwareListParams(filters),
    signal,
  });
  return data;
}

export async function listFirmwareFilterOptions({ signal } = {}) {
  const { data } = await api.get(`${FIRMWARE_PATH}/filter-options`, { signal });
  return data?.data || data || {};
}

export async function createFirmware(payload) {
  const { data } = await api.post(FIRMWARE_PATH, payload);
  return data;
}

export async function updateFirmware(firmwareId, payload) {
  const { data } = await api.patch(
    `${FIRMWARE_PATH}/${encodeURIComponent(firmwareId)}`,
    payload
  );
  return data;
}

export async function deleteFirmware(firmwareId) {
  const { data } = await api.delete(
    `${FIRMWARE_PATH}/${encodeURIComponent(firmwareId)}`
  );
  return data;
}

function safeXlsxFilename(value) {
  if (typeof value !== "string") return null;
  const filename = value.trim();
  if (
    !filename ||
    filename === "." ||
    filename === ".." ||
    filename.includes("/") ||
    filename.includes("\\") ||
    /[\u0000-\u001f\u007f]/.test(filename) ||
    !filename.toLowerCase().endsWith(".xlsx")
  ) {
    return null;
  }
  return filename;
}

export function getFirmwareXlsxFilename(contentDisposition, fallback) {
  const safeFallback = safeXlsxFilename(fallback) || FIRMWARE_EXPORT_FALLBACK_FILENAME;
  if (typeof contentDisposition !== "string") return safeFallback;

  const encodedMatch = contentDisposition.match(
    /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i
  );
  if (encodedMatch) {
    try {
      const decoded = decodeURIComponent(
        encodedMatch[1].trim().replace(/^"|"$/g, "")
      );
      const safeDecoded = safeXlsxFilename(decoded);
      if (safeDecoded) return safeDecoded;
    } catch {
      // Fall through to the regular filename or safe fallback.
    }
  }
  const filenameMatch = contentDisposition.match(
    /filename\s*=\s*(?:"([^"]+)"|([^;]+))/i
  );
  return safeXlsxFilename(filenameMatch?.[1] || filenameMatch?.[2]) || safeFallback;
}

export async function normalizeFirmwareBlobError(error) {
  const data = error?.response?.data;
  if (!(typeof Blob !== "undefined" && data instanceof Blob)) return error;
  try {
    error.response = { ...error.response, data: JSON.parse(await data.text()) };
  } catch {
    // Preserve the Blob response when it is not JSON.
  }
  return error;
}

function firmwareFile(response, fallback) {
  return {
    blob: response.data,
    filename: getFirmwareXlsxFilename(
      response.headers?.["content-disposition"],
      fallback
    ),
    deletedCount: Number(response.headers?.["x-firmware-deleted-count"] || 0),
    snapshotHash: response.headers?.["x-firmware-snapshot-hash"] || "",
  };
}

export async function exportFirmware(filters = {}) {
  try {
    const response = await api.get(`${FIRMWARE_PATH}/export`, {
      params: buildFirmwareExportParams(filters),
      responseType: "blob",
    });
    return firmwareFile(response, FIRMWARE_EXPORT_FALLBACK_FILENAME);
  } catch (error) {
    throw await normalizeFirmwareBlobError(error);
  }
}

export function downloadFirmwareXlsx(
  { blob, filename },
  { documentRef = document, urlApi = URL } = {}
) {
  const objectUrl = urlApi.createObjectURL(blob);
  let anchor;
  try {
    anchor = documentRef.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename || FIRMWARE_EXPORT_FALLBACK_FILENAME;
    anchor.style.display = "none";
    documentRef.body.appendChild(anchor);
    anchor.click();
  } finally {
    anchor?.remove();
    urlApi.revokeObjectURL(objectUrl);
  }
}

function firmwareUpload(file) {
  const formData = new FormData();
  formData.append("file", file);
  return formData;
}

export async function previewFirmwareImport(file) {
  const { data } = await api.post(
    `${FIRMWARE_PATH}/import/preview`,
    firmwareUpload(file)
  );
  return data?.data || data;
}

export async function applyFirmwareImport(file) {
  const { data } = await api.post(
    `${FIRMWARE_PATH}/import/apply`,
    firmwareUpload(file)
  );
  return data;
}

export async function previewFirmwareDeleteAll() {
  const { data } = await api.get(`${FIRMWARE_PATH}/delete-all/preview`);
  return data?.data || data;
}

export async function deleteAllFirmware(payload) {
  try {
    const response = await api.post(`${FIRMWARE_PATH}/delete-all`, payload, {
      responseType: "blob",
    });
    return firmwareFile(response, FIRMWARE_BACKUP_FALLBACK_FILENAME);
  } catch (error) {
    throw await normalizeFirmwareBlobError(error);
  }
}
